import { useEffect, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

export type LoaderProps = {
  image?: string;
  messages: string[];
  maxLoadTime?: number;
  messageRotationInterval?: number;
};

const calculatePercentComplete = (elapsed: number, maxLoadTime: number) =>
  (1 - 10 ** ((-2.3 * elapsed) / maxLoadTime)) * 100;

export function Loader({
  image,
  messages,
  maxLoadTime,
  messageRotationInterval,
}: LoaderProps) {
  const maxLoadTimeMs = useMemo(
    () => (!!maxLoadTime ? maxLoadTime * 1000 : 0),
    [maxLoadTime]
  );
  const timerStarted = useMemo(() => Date.now(), []);

  const [elapsed, setElapsed] = useState<number>(
    () => Date.now() - timerStarted
  );
  const [percentComplete, setPercentComplete] = useState<number>(
    calculatePercentComplete(elapsed, maxLoadTimeMs)
  );
  const [messageIndex, setMessageIndex] = useState<number>(0);

  const canBeLongerThanOneMinute = useMemo(() => {
    return !maxLoadTime || maxLoadTime >= 60;
  }, [maxLoadTime]);

  const selectedMessage = useMemo(
    () => messages[messageIndex] || messages[0] || "Loading...",
    [messageIndex, messageIndex]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setElapsed(Date.now() - timerStarted);
      const updated = calculatePercentComplete(elapsed, maxLoadTimeMs);

      setPercentComplete(updated);
    }, 250);

    return () => clearTimeout(timer);
  }, [percentComplete, timerStarted, elapsed, maxLoadTimeMs]);

  useEffect(() => {
    const timer = setTimeout(
      () => {
        if (messageIndex !== messages.length - 1) {
          setMessageIndex(messageIndex + 1);
        }
      },
      (messageRotationInterval || 4) * 1000
    );

    return () => clearTimeout(timer);
  }, [messageIndex, messages.length, messageRotationInterval]);

  return (
    <Box
      maxWidth={392}
      display="flex"
      flexDirection="column"
      marginX="auto"
      alignItems="center"
      alignContent="center"
      justifyItems="center"
      textAlign="center">
      <Box
        mb={5}
        height="288px"
        width="288px"
        display="flex"
        flexDirection="column"
        m="auto"
        alignItems="center"
        justifyContent="center">
        {image && (
          <img src={image} alt="loading" style={{ position: "absolute" }} />
        )}
        {!!maxLoadTime && (
          <Typography variant="h3">
            <NumericFormat
              displayType="text"
              decimalScale={0}
              suffix="%"
              value={percentComplete}
            />
          </Typography>
        )}
      </Box>
      <Box px={{ xs: 4, sm: 0 }}>
        <Typography variant="h3" data-testid="just-wait-message">
          {selectedMessage}
        </Typography>
      </Box>
      {canBeLongerThanOneMinute && (
        <Box mt={3}>
          <Typography variant="subtitle1">
            This might take a few minutes. Please be patient and don&apos;t
            close your browser.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
